/* eslint-disable import/no-unresolved */
import { ChevronRightIcon } from "@chakra-ui/icons";
import { Box, Heading, HStack, Stack, Text, VStack } from "@chakra-ui/react";
import { FormattedMessage } from "@react-intl";
import { IItems } from "@the-volte/svc-core-sdk/lib/api/Cart/types";
import { ISelectedProductInput } from "@the-volte/svc-core-sdk/lib/helpers";
import { ProductDetails_product } from "@the-volte/svc-core-sdk/lib/queries/gqlTypes/ProductDetails";
import { add } from "date-fns";
import dayjs from "dayjs";
import { get, isEmpty, isNaN } from "lodash";
import Image from "next/image";
import { useRouter } from "next/router";
import React, { ReactNode, useContext, useState } from "react";

import { useDatalayer } from "@app/providers/DatalayerProvider";
import { Button } from "@components/atoms";
import { Money } from "@components/containers";
import { ShopContext } from "@components/legacy/ShopProvider/context";
import { defaultTheme } from "@styles";
import { decodeBase64Id } from "@utils/base64";
import { formatMoney } from "@utils/format-money";
import { getAttributeValue } from "@utils/getAttributeValue";

import { channelSlug } from "../../../../constants";
import AfterPay from "../../../../images/afterpay.svg";
import ClearPay from "../../../../images/clearpay.svg";
import {
  canAddToCart,
  getAfterpayPrice,
  getProductPrice,
} from "../stockHelpers";
import * as S from "../styles";
import { CheckoutButton } from "./CheckoutButton";

interface IAddToCartSection {
  product?: ProductDetails_product | null;
  productVariants?: ProductDetails_product["variants"];
  productAttributes?: ProductDetails_product["attributes"];
  name: string;
  designer: string;
  designerSlug: string;
  bookedDates: Date[];
  productPricing?: ProductDetails_product["pricing"];
  items: IItems;
  isAvailableForPurchase?: boolean | null;
  variantId: string;
  onSubmit({
    variantId,
    quantity,
    rentalPeriod,
    bookedRange,
  }: ISelectedProductInput): void;
  productBookingsLoading: boolean;
  isSamedayEligible?: boolean;
  canPickupToday?: boolean;
  onRentClick: () => void;
}

const AddToCartBoxes = ({ children }: { children: ReactNode }) => (
  <Stack
    border="1px solid"
    borderColor={defaultTheme.colors.secondaryOverlayDark}
    p={4}
    borderRadius="md"
    width="full"
  >
    {children}
  </Stack>
);

export const AddToCartSectionDesignB: React.FC<IAddToCartSection> = ({
  isAvailableForPurchase,
  items,
  name,
  designer,
  designerSlug,
  bookedDates,
  product,
  productPricing,
  productVariants,
  productAttributes,
  onSubmit,
  variantId,
  productBookingsLoading,
  isSamedayEligible = false,
  canPickupToday = true,
  onRentClick,
}) => {
  const router = useRouter();

  const [submitting, setSubmitting] = useState(false);
  const { push } = useDatalayer();
  const { currency } = useContext(ShopContext);

  const selectedVariant = productVariants?.find((pv) => pv?.id === variantId);

  const preparationDays = product?.resource?.preparationDays;

  const disabledDaysSortedAsc = bookedDates.sort(
    (objA, objB) => Number(objA) - Number(objB)
  );

  const today = new Date();

  // This is for the date where you can book the item
  let latestDate = !isEmpty(bookedDates)
    ? disabledDaysSortedAsc[disabledDaysSortedAsc.length - 1]
    : add(today, { days: 1 });
  latestDate = add(latestDate, { days: preparationDays });

  // Reset the date to 12am
  latestDate.setHours(12, 0, 0, 0);

  const outOfStock = Number(selectedVariant?.quantityAvailable) <= 0;

  const maximumShippingDays = get(
    product,
    "shippingMethods[0].maximumDeliveryDays",
    0
  );

  const minimumShippingDays = get(
    product,
    "shippingMethods[0].minimumDeliveryDays",
    0
  );

  const disableButton =
    // eslint-disable-next-line strict-null-checks/all
    !canAddToCart(items || [], !!isAvailableForPurchase, variantId, 1) ||
    outOfStock;

  const { deliveryDate } = router.query;

  const startDate =
    deliveryDate && typeof deliveryDate === "string"
      ? new Date(deliveryDate)
      : null;

  startDate?.setHours(12, 0, 0, 0);

  if (startDate && startDate.getTime() > latestDate.getTime())
    latestDate = startDate;

  const productSize = React.useMemo(() => {
    let productSizeStr = "";
    const productSizeAttr =
      productAttributes &&
      productAttributes?.length > 0 &&
      productAttributes.find((attr) => attr.attribute.name === "Size");
    if (productSizeAttr && productSizeAttr?.values?.length > 0) {
      productSizeStr = productSizeAttr.values
        .map((value) => value?.name)
        .join(", ");
    }
    return productSizeStr;
  }, [productAttributes]);

  const purchaseVariant = productVariants?.find(
    (productPurchaseVariant) => productPurchaseVariant?.name === "Purchase"
  );

  const productRetailValue = Number(product?.retailValue?.amount);
  const productPurchasePrice =
    Number(purchaseVariant?.pricing?.price?.gross?.amount) +
    Number(purchaseVariant?.pricing?.borrowerFee?.gross?.amount);
  const retailSavings = productRetailValue - productPurchasePrice;
  const originalPurchasePrice = product?.originalPurchasePrice?.amount;

  let moneySaved = 0;

  if (
    originalPurchasePrice &&
    productPurchasePrice &&
    originalPurchasePrice > productPurchasePrice &&
    product.hasWarehouseSale
  ) {
    moneySaved = originalPurchasePrice - productPurchasePrice;
  }

  const pushToDataLayer = () => {
    const selectedVariantPrice =
      selectedVariant?.pricing?.price?.gross?.amount ?? 0;
    const borrowerFee =
      selectedVariant?.pricing?.borrowerFee?.gross?.amount ?? 0;
    const totalPrice = selectedVariantPrice + borrowerFee;

    if (product && selectedVariant && !!totalPrice) {
      push({
        event: "add_to_cart",
        ecommerce: {
          currency,
          value: totalPrice,
          items: [
            {
              item_id: decodeBase64Id(product.id),
              item_brand: getAttributeValue("designer", product.attributes),
              item_category: product.category?.name ?? "",
              item_name: product.name,
              item_variant: `Size ${getAttributeValue(
                "size",
                product.attributes
              )}`,
              price: totalPrice,
              quantity: 1,
            },
          ],
        },
      });
    }
  };

  return (
    <>
      {/* Header and Size */}
      <VStack
        gap={{ base: 0, ml: 2 }}
        alignItems="flex-start"
        mt={{ base: 4, lg: 0 }}
        mb={12}
      >
        <S.DesignerHeader
          data-test="designer"
          href={`/designers/${
            designerSlug.endsWith("-hire")
              ? designerSlug
              : designerSlug.concat("-hire")
          }`}
        >
          {designer}
          <ChevronRightIcon />
        </S.DesignerHeader>
        <Heading
          as="h1"
          data-test="productName"
          fontSize={{ base: 20, md: 24 }}
        >
          {name}
        </Heading>

        <Text fontSize={16} data-test="productSize">
          Size {productSize}
        </Text>
      </VStack>

      <VStack width="full" gap={6} w="full" maxW="65ch" margin="0 auto">
        {/* Purchase Price Box */}
        <AddToCartBoxes>
          <Heading
            as="h2"
            fontSize={{ base: 18, md: 20 }}
            justifyContent="start"
            display="flex"
            alignItems="center"
          >
            {!!productPricing &&
              getProductPrice(
                productPricing,
                purchaseVariant?.pricing as any
              )}{" "}
            {moneySaved && (
              <>
                <span
                  style={{
                    fontSize: 15,
                    color: "grey",
                    textDecoration: "line-through",
                    marginLeft: 5,
                  }}
                >
                  <Money
                    money={{
                      amount: product?.originalPurchasePrice?.amount as number,
                      currency: product?.originalPurchasePrice?.currency || "",
                    }}
                    showDecimals
                  />
                </span>
                <span style={{ fontSize: 15, color: "#d7362c", marginLeft: 5 }}>
                  (
                  <FormattedMessage defaultMessage="Save" />{" "}
                  <Money
                    money={{
                      amount: moneySaved as number,
                      currency: product?.originalPurchasePrice?.currency || "",
                    }}
                    showDecimals
                  />
                  )
                </span>
              </>
            )}
          </Heading>

          <Text
            sx={{ display: "flex", alignItems: "center" }}
            fontSize={{ base: 12, md: 14 }}
            color="#a1a1a1"
          >
            or 4 payments of&nbsp;
            {!!productPricing &&
              getAfterpayPrice(productPricing, purchaseVariant?.pricing as any)}
            &nbsp;with&nbsp;
            {channelSlug !== "volte-uk" ? (
              <Image
                src={AfterPay.src}
                width={90}
                height={25}
                alt="afterpay-logo"
              />
            ) : (
              <Box alignItems="center" display="flex" paddingX={1}>
                <Image
                  src={ClearPay.src}
                  width={85}
                  height={20}
                  alt="clearpay-logo"
                />
              </Box>
            )}
          </Text>

          {!isNaN(productRetailValue) && retailSavings > 1 && !moneySaved ? (
            <HStack fontSize={14} gap={2} alignItems="center">
              <Text>
                Retail:&nbsp;
                {formatMoney(
                  productRetailValue,
                  purchaseVariant?.pricing?.price?.gross?.currency
                )}
              </Text>
              <Text color="green.500">
                Save&nbsp;
                {formatMoney(
                  retailSavings,
                  purchaseVariant?.pricing?.price?.gross?.currency
                )}
              </Text>
            </HStack>
          ) : null}

          {/* PURCHASE BUTTON */}
          {!productBookingsLoading && (
            <S.VariantPickerB>
              <CheckoutButton
                isPurchase
                defaultText="BUY NOW"
                disabledDays={
                  [
                    ...bookedDates,
                    !isSamedayEligible
                      ? {
                          before: dayjs()
                            .startOf("date")
                            .add(2, "day")
                            .toDate(),
                        }
                      : canPickupToday
                      ? { before: dayjs().startOf("date").toDate() }
                      : {
                          before: dayjs()
                            .startOf("date")
                            .add(1, "day")
                            .toDate(),
                        },
                  ] as any
                }
                dateFilterStartDate={startDate || undefined}
                daysRange={purchaseVariant?.rentalDays || 365}
                disabled={!purchaseVariant?.rentalDays || disableButton}
                onSubmit={async ({ from, to }) => {
                  if (!purchaseVariant?.id) return;

                  setSubmitting(true);
                  pushToDataLayer();

                  await onSubmit({
                    variantId: purchaseVariant.id,
                    quantity: 1,
                    rentalPeriod: purchaseVariant.rentalDays,
                    bookedRange: { from, to },
                  } as ISelectedProductInput);
                }}
                sleepTime={500}
                preparationDays={preparationDays || 3}
                maximumShippingDays={maximumShippingDays}
                minimumShippingDays={minimumShippingDays}
                samedayPickupEnabled={isSamedayEligible}
                canPickupToday={canPickupToday}
              />
            </S.VariantPickerB>
          )}
        </AddToCartBoxes>

        {/* Rent Now Box */}
        <AddToCartBoxes>
          <Heading as="h2" fontSize={{ base: 18, md: 18 }}>
            Interested in renting this item?
          </Heading>
          <S.VariantPickerB>
            <Button
              sx={{
                marginTop: "15px",
              }}
              onClick={onRentClick}
              disabled={submitting}
            >
              RENT NOW
            </Button>
          </S.VariantPickerB>
        </AddToCartBoxes>
      </VStack>
    </>
  );
};
